/**
 * McpConnectorsSection — the "Custom connectors" section of the agent editor's
 * Connectors tab: remote MCP servers plugged into THIS agent by URL, the way
 * Claude takes a custom connector.
 *
 * Unlike the Drive switches above it, nothing here rides the agent form. A
 * connector is its own record (see backend/modules/mcp_connectors): adding one
 * handshakes with the server and stores its tool list right away, so there is
 * no "Save" to forget and no secret round-tripping through the form. Which is
 * also why a NEW agent has to be saved first — there is no id to hang a
 * connector on until then.
 *
 * Everyone who can chat with the agent gets the server's tools through it;
 * only people who can edit the agent see this section at all (the API 403s
 * anyone else).
 *
 * The rows live in the tab's one Services list (BotConnectorsTab), whose Add
 * opens the organization's list; its "Custom MCP server" opens this form.
 * The same form edits a list entry's DEFAULTS (`McpConnectorForm` in
 * `mode="defaults"`, the library entry window) — one editor, two homes. Every
 * connector carries its status chip (shared / customized / this agent only);
 * changing the tools of a shared one asks first, because that makes it
 * customized and it stops getting the organization's updates.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Switch, Tooltip } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { showNotification } from '@/utils/showNotification';
import {
  mcpConnectorService,
  type McpConnector,
  type McpConnectorErrorBody,
} from '../services/mcpConnectorService';
import {
  DialogColumn, DialogColumns, Field, PrimaryAction, ReadOnlyValue, RiskAcknowledgement,
  connectorErrorBody, dialogFrame, isSaveCancelled, phraseConnectorError, switchesOnWrites, useRiskAcknowledgement,
  type FormFrame,
} from './ConnectorDialog';
import { SignInChoice } from './SignInChoice';
import { NO_SIGN_IN, mcpHeaders, signInComplete, type SignInValue } from '../utils/signIn';
import { GuideDraft, type DraftScope } from './GuideDraft';
import type { AgentConnectorLibrary } from '@modules/connector_library/hooks/useAgentConnectorLibrary';
import { useConfirmStep } from '@modules/connector_library/hooks/useConfirmStep';
import { LibraryConnectorStatus } from '@modules/connector_library/components/ConnectorStatus';
import { CredentialFields, ServiceIcon } from '@modules/connector_library/components/LibraryBits';
import {
  LIBRARY_DESCRIPTION_MAX, withLibraryStatus,
} from '@modules/connector_library/services/connectorLibraryService';

interface Props {
  /** Undefined while the agent has not been saved yet. */
  chatbotId?: string;
  disabled?: boolean;
  /** The organization's connector list, when this agent may use it. */
  library?: AgentConnectorLibrary;
  /** The custom form is open (the list's "Custom MCP server"). */
  customAddOpen?: boolean;
  onCustomAddClose?: () => void;
  /** How many connectors it lists, for the tab's empty state. */
  onCount?: (count: number) => void;
}

/** Mirrors MAX_NAME_LENGTH in backend/modules/mcp_connectors/services/mcp_connector_service.py. */
const MAX_NAME_LENGTH = 80;

const errorBody = (err: unknown): McpConnectorErrorBody => connectorErrorBody(err) as McpConnectorErrorBody;

export default function McpConnectorsSection({
  chatbotId, disabled, library, customAddOpen, onCustomAddClose, onCount,
}: Props) {
  const { t } = useLanguage();
  const [connectors, setConnectors] = useState<McpConnector[]>([]);
  // A count only from a list that arrived: a failed read is not "none".
  const [listed, setListed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<McpConnector | null>(null);
  /** The connector whose guide is open for editing on its row. */
  const [guideFor, setGuideFor] = useState<string | null>(null);
  /** A tool that changes data, switched on and waiting for the acknowledgement on its row (item 69). */
  const [askingWrite, setAskingWrite] = useState<{ connectorId: string; tool: string; ticked: boolean } | null>(null);
  const { confirm, modal: confirmModal } = useConfirmStep();

  const load = useCallback(async () => {
    if (!chatbotId) return;
    try {
      setConnectors(await mcpConnectorService.list(chatbotId));
      setListed(true);
    } catch {
      setListed(false);
      showNotification(t('bots.connectors.mcp.loadError'), 'error');
    }
  }, [chatbotId, t]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => { if (listed) onCount?.(connectors.length); }, [listed, connectors.length, onCount]);

  const closeAdd = () => onCustomAddClose?.();

  const replace = (next: McpConnector) =>
    setConnectors(prev => prev.map(c => (c.connector_id === next.connector_id ? withLibraryStatus(c, next) : c)));

  /** Changing a shared connector on this agent makes it customized: ask first. */
  const keepsSharing = async (c: McpConnector, confirmText: string, message: string): Promise<boolean> => {
    if (c.library?.chip !== 'shared') return true;
    return confirm({
      title: t('connectorLibrary.customize.title', { name: c.name }),
      message,
      confirmText,
      cancelText: t('common.cancel'),
      variant: 'warning',
    });
  };

  const phrase = (err: unknown, fallbackKey: string): string => phraseConnectorError(t, err, fallbackKey);

  /** Run one row's action with the row marked busy; a failure is reported
      the same way for every action. */
  const withBusy = async (c: McpConnector, fn: () => Promise<void>) => {
    setBusyId(c.connector_id);
    try {
      await fn();
    } catch (err) {
      // A failed refresh is also written on the row; show the row as it now is.
      const failedRow = errorBody(err).connector;
      if (failedRow) replace(failedRow);
      showNotification(phrase(err, 'bots.connectors.mcp.updateError'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const toggleEnabled = (c: McpConnector) => withBusy(c, async () => {
    replace(await mcpConnectorService.update(chatbotId!, c.connector_id, { enabled: !c.enabled }));
  });

  /** Switching a tool that changes data ON waits for the acknowledgement on its row;
      everything else (and switching off) saves at once. */
  const toggleTool = async (c: McpConnector, toolName: string, acknowledged = false) => {
    const tool = c.tools.find(tl => tl.name === toolName);
    if (tool && !tool.enabled && tool.writes && !acknowledged) {
      setAskingWrite({ connectorId: c.connector_id, tool: toolName, ticked: false });
      return;
    }
    if (!(await keepsSharing(c, t('common.save'), t('connectorLibrary.customize.body')))) return;
    await withBusy(c, async () => {
      const off = new Set(c.tools.filter(tl => !tl.enabled).map(tl => tl.name));
      if (off.has(toolName)) off.delete(toolName); else off.add(toolName);
      replace(await mcpConnectorService.update(chatbotId!, c.connector_id, {
        disabled_tools: [...off], ...(acknowledged ? { acknowledge_write: true } : {}),
      }));
      setAskingWrite(null);
    });
  };

  const saveGuide = async (c: McpConnector, text: string) => {
    if (!(await keepsSharing(c, t('common.save'), t('connectorLibrary.customize.guideBody')))) return;
    await withBusy(c, async () => {
      replace(await mcpConnectorService.update(chatbotId!, c.connector_id, { instructions: text.trim() }));
      setGuideFor(null);
      showNotification(t('bots.connectors.mcp.guideSaved'), 'success');
    });
  };

  const refresh = async (c: McpConnector) => {
    if (!(await keepsSharing(c, t('bots.connectors.mcp.refresh'), t('connectorLibrary.customize.refreshBody')))) return;
    await withBusy(c, async () => {
      replace(await mcpConnectorService.refresh(chatbotId!, c.connector_id));
      showNotification(t('bots.connectors.mcp.refreshed'), 'success');
    });
  };

  const confirmRemove = () => {
    const c = pendingRemove;
    if (!c) return;
    setPendingRemove(null);
    void withBusy(c, async () => {
      await mcpConnectorService.remove(chatbotId!, c.connector_id);
      setConnectors(prev => prev.filter(x => x.connector_id !== c.connector_id));
      if (c.library_entry_id) void library?.reload();
    });
  };

  const agentBackend = useMemo(() => (chatbotId ? agentMcpBackend(chatbotId) : null), [chatbotId]);

  return (
    <div data-testid="mcp-connectors-section">
      {connectors.length > 0 && (
        <div className="cxs__list">
          {connectors.map(c => {
            const busy = busyId === c.connector_id;
            const offCount = c.tools.filter(tl => !tl.enabled).length;
            const expanded = expandedId === c.connector_id;
            return (
              <LibraryConnectorStatus key={c.connector_id} chatbotId={chatbotId!} kind="mcp" connector={c}
                                      disabled={disabled || busy} onChanged={replace}>
                {({ chips, panel }) => (
                  <div className={`cxc cxc--column ${c.enabled ? 'is-on' : ''} ${busy ? 'is-busy' : ''}`}
                       data-testid={`mcp-connector-${c.slug}`}>
                    <div className="cxc__row">
                      <ServiceIcon name={c.name} slug={c.slug} url={c.url} className="cxc__icon"
                                   fallback={<span className="cxc__icon" aria-hidden="true"><PlugGlyph /></span>} />
                      <span className="cxc__body">
                        <span className="cxc__name" dir="auto">{c.name}</span>
                        <span className="cxc__meta">
                          {/* What the server calls itself is detail: on hover, not a line of its own. */}
                          <span className="cxc__url" dir="ltr"
                                title={c.server_name ? `${c.server_name}${c.server_version ? ` ${c.server_version}` : ''}` : undefined}>
                            {c.url}
                          </span>
                        </span>
                        <span className="cxc__pills">
                          {c.status === 'error' ? (
                            <Tooltip title={c.last_error || ''}>
                              <span className="cx-pill cx-pill--error" data-testid="mcp-status-error">
                                {t('bots.connectors.mcp.statusError')}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="cx-pill cx-pill--ok" data-testid="mcp-status-ok">
                              {t('bots.connectors.mcp.statusConnected')}
                            </span>
                          )}
                          {c.has_headers && <span className="cx-pill">{t('bots.connectors.mcp.hasAuth')}</span>}
                          {chips}
                          <button type="button" className="cxc__link"
                                  onClick={() => setExpandedId(expanded ? null : c.connector_id)}
                                  aria-expanded={expanded} data-testid={`mcp-tools-toggle-${c.slug}`}>
                            {offCount > 0
                              ? t('bots.connectors.mcp.toolCountSomeOff', { count: c.tools.length, off: offCount })
                              : t('bots.connectors.mcp.toolCount', { count: c.tools.length })}
                          </button>
                        </span>
                      </span>
                      <span className="cxc__end">
                        <span className="cxc__actions">
                          <button type="button" className="cx-btn cx-btn--sm" disabled={disabled || busy}
                                  onClick={() => setGuideFor(guideFor === c.connector_id ? null : c.connector_id)}
                                  aria-expanded={guideFor === c.connector_id} data-testid={`mcp-guide-${c.slug}`}>
                            {t('bots.connectors.mcp.guide')}
                          </button>
                          <button type="button" className="cx-btn cx-btn--sm" disabled={disabled || busy}
                                  onClick={() => { void refresh(c); }} data-testid={`mcp-refresh-${c.slug}`}>
                            {t('bots.connectors.mcp.refresh')}
                          </button>
                          <button type="button" className="cx-btn cx-btn--sm cx-btn--danger" disabled={disabled || busy}
                                  onClick={() => setPendingRemove(c)} data-testid={`mcp-remove-${c.slug}`}>
                            {t('common.remove')}
                          </button>
                        </span>
                        <Switch checked={c.enabled} disabled={disabled || busy} onChange={() => toggleEnabled(c)}
                                data-testid={`mcp-enabled-${c.slug}`} />
                      </span>
                    </div>

                    {panel}

                    {guideFor === c.connector_id && (
                      <GuideEditor chatbotId={chatbotId!} connector={c} busy={busy} disabled={disabled}
                                   onCancel={() => setGuideFor(null)}
                                   onSave={(text) => saveGuide(c, text)} />
                    )}

                    {expanded && (
                      <ul className="cxc__tools" data-testid={`mcp-tools-${c.slug}`}>
                        {c.tools.map(tool => {
                          const asking = askingWrite?.connectorId === c.connector_id && askingWrite.tool === tool.name
                            ? askingWrite : null;
                          return (
                          <li key={tool.name} className={`cxc__tool ${tool.enabled ? '' : 'is-off'} ${asking ? 'is-asking' : ''}`}>
                            <span className="cxc__tool-text">
                              <code className="cxc__tool-name" dir="ltr">{tool.name}</code>
                              {tool.description && <span className="cxc__tool-desc bidi-clamp" dir="auto">{tool.description}</span>}
                            </span>
                            <Switch size="small" checked={tool.enabled || !!asking} disabled={disabled || busy || !c.enabled}
                                    onChange={() => { if (asking) setAskingWrite(null); else void toggleTool(c, tool.name); }}
                                    data-testid={`mcp-tool-${c.slug}-${tool.name}`} />
                            {asking && (
                              <div className="cxc__tool-ack" data-testid={`mcp-tool-ack-${c.slug}-${tool.name}`}>
                                <RiskAcknowledgement risk="write" service={c.name} checked={asking.ticked}
                                                     onChange={v => setAskingWrite({ ...asking, ticked: v })} />
                                <span className="cxc__tool-ack-actions">
                                  <button type="button" className="cx-btn cx-btn--sm" disabled={busy}
                                          onClick={() => setAskingWrite(null)}>
                                    {t('common.cancel')}
                                  </button>
                                  <button type="button" className="cx-btn cx-btn--sm cx-btn--primary"
                                          disabled={busy || !asking.ticked}
                                          onClick={() => { void toggleTool(c, tool.name, true); }}
                                          data-testid={`mcp-tool-ack-save-${c.slug}-${tool.name}`}>
                                    {t('bots.connectors.ack.switchOn')}
                                  </button>
                                </span>
                              </div>
                            )}
                          </li>
                          );
                        })}
                      </ul>
                )}
              </div>
                )}
              </LibraryConnectorStatus>
            );
          })}
        </div>
      )}

      {chatbotId && agentBackend && customAddOpen && (
        <McpConnectorForm
          backend={agentBackend}
          draftScope={{ chatbotId }}
          existing={null}
          onClose={closeAdd}
          onSaved={(c) => { setConnectors(prev => [...prev, c]); closeAdd(); }}
          phrase={phrase}
        />
      )}

      {confirmModal}

      <ConfirmModal
        isOpen={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={confirmRemove}
        title={t('bots.connectors.mcp.removeTitle', { name: pendingRemove?.name ?? '' })}
        message={t('bots.connectors.mcp.removeBody')}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        variant="danger"
      />
    </div>
  );
}

/** Where the form saves to: an agent's endpoints, or the list's. */
export interface McpConnectorFormBackend<T> {
  create: (payload: Record<string, unknown>) => Promise<T>;
  update?: (id: string, payload: Record<string, unknown>) => Promise<T>;
}

/** A saved list entry, as the defaults form edits it. */
export interface McpConnectorFormExisting {
  id: string;
  name: string;
  description?: string;
  url: string;
  auth_header_name?: string | null;
  has_shared_credential?: boolean;
  tools?: { name: string; description: string; enabled: boolean; writes?: boolean }[];
  instructions?: string;
}

/** The agent's endpoints, as a form backend (create only: an agent's
    connector is changed from its row — tools, refresh, on/off). */
function agentMcpBackend(chatbotId: string): McpConnectorFormBackend<McpConnector> {
  return {
    create: (payload) => mcpConnectorService.create(chatbotId, payload as never),
  };
}

/** Mounted only while open, so every opening starts from its own fields. */
interface FormProps<T> {
  backend: McpConnectorFormBackend<T>;
  /** Where the connector is edited (an agent, or a library entry): "Write a draft" is allowed there. */
  draftScope: DraftScope;
  existing: McpConnectorFormExisting | null;
  onClose: () => void;
  onSaved: (saved: T) => void;
  phrase: (err: unknown, fallbackKey: string) => string;
  /** 'defaults' edits an entry of an organization's connector list; how it
      signs in is the entry's Sign-in tab's (item 80). */
  mode?: 'agent' | 'defaults';
  /** Defaults mode: the one line saying how it signs in, with the way to the Sign-in tab. */
  signInSummary?: ReactNode;
  banner?: ReactNode;
  footerMeta?: ReactNode;
  /** Defaults mode, an entry inherited from an organization above: only the
      name, description and tool switches are this organization's to change
      — the address and the credential are shown, "Set by <owner>". */
  layer?: { ownerName: string };
  /** Where the form is drawn: its own dialog (default), or a host's frame. */
  frame?: FormFrame;
}

export function McpConnectorForm<T>({
  backend, existing, onClose, onSaved, phrase, mode = 'agent', banner, footerMeta, layer, frame = dialogFrame,
  draftScope, signInSummary,
}: FormProps<T>) {
  const { t } = useLanguage();
  const defaultsMode = mode === 'defaults';
  const layerMode = defaultsMode && !!layer && !!existing;
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  // The agent's own key, where the server expects it (a list entry's is its Sign-in tab's).
  const [signIn, setSignIn] = useState<SignInValue>(NO_SIGN_IN);
  const [offTools, setOffTools] = useState<Set<string>>(
    () => new Set((existing?.tools || []).filter(tl => !tl.enabled).map(tl => tl.name)));
  const [guide, setGuide] = useState(existing?.instructions ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The risk acknowledgement beside Save (item 69): a server added here by
  // hand, or tools that change data switched on by this save.
  const ack = useRiskAcknowledgement({
    customAdd: !existing && !defaultsMode,
    switchesOn: switchesOnWrites((existing?.tools || []).map(tl => ({
      writes: tl.writes, was: tl.enabled, now: !offTools.has(tl.name) }))),
    service: name.trim() || existing?.name,
  });

  const typedHeaders = mcpHeaders(signIn);
  const signInMissing = !defaultsMode && !signInComplete('agent', signIn, false);
  // A shared key goes only to the address it was entered for: changing it drops the key.
  const urlChanged = !!existing && url.trim() !== existing.url;
  const [addressKey, setAddressKey] = useState('');
  // A new address of a server that takes a key is handshaken with one typed here (the stored one never goes there).
  const asksAddressKey = defaultsMode && !layerMode && urlChanged && !!existing?.auth_header_name;
  const addressKeyPayload = asksAddressKey && addressKey.trim() && existing?.auth_header_name
    ? { headers: { [existing.auth_header_name]: addressKey.trim() }, share_credential: !!existing.has_shared_credential } : {};

  const submit = async () => {
    setError(null);
    setSaving(true);
    try {
      if (existing && backend.update && layerMode) {
        // An inherited entry: only what this organization may change.
        onSaved(await backend.update(existing.id, {
          name: name.trim(), description: description.trim(), disabled_tools: [...offTools],
          instructions: guide.trim(), ...ack.payload,
        }));
      } else if (existing && backend.update) {
        const payload: Record<string, unknown> = {
          name: name.trim(),
          description: description.trim(),
          disabled_tools: [...offTools],
          instructions: guide.trim(),
          ...(url.trim() !== existing.url ? { url: url.trim() } : {}),
          ...addressKeyPayload,
          ...ack.payload,
        };
        onSaved(await backend.update(existing.id, payload));
      } else {
        const created = await backend.create({
          name: name.trim(), url: url.trim(), headers: typedHeaders,
          ...(guide.trim() ? { instructions: guide.trim() } : {}),
          ...(defaultsMode ? { description: description.trim() } : {}),
          ...ack.payload,
        });
        const tools = (created as unknown as { tools?: unknown[] }).tools || [];
        showNotification(t('bots.connectors.mcp.connected', { count: tools.length }), 'success');
        onSaved(created);
      }
    } catch (err) {
      ack.onError(err);
      if (!isSaveCancelled(err)) setError(phrase(err, 'bots.connectors.mcp.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // What the form held when it opened: an existing entry's Save stays off
  // until something actually changed (the library entry's footer says so).
  const snapshot = JSON.stringify([name, description, url, signIn, addressKey, [...offTools].sort(), guide]);
  const opened = useRef(snapshot);
  const dirty = snapshot !== opened.current;
  const canSubmit = name.trim().length > 0 && url.trim().length > 0 && !signInMissing && !saving
    && (!existing || dirty) && ack.ok;
  const toggleTool = (toolName: string) => setOffTools(prev => {
    const next = new Set(prev);
    if (next.has(toolName)) next.delete(toolName); else next.add(toolName);
    return next;
  });

  const setBy = layer ? t('connectorLibrary.editor.setBy', { organization: layer.ownerName }) : undefined;

  return frame({
    size: 'medium',
    icon: <PlugGlyph />,
    // Defaults mode is drawn by the library entry window, which titles it itself.
    title: defaultsMode ? '' : t('bots.connectors.mcp.addTitle'),
    subtitle: defaultsMode ? undefined : t('bots.connectors.mcp.addHint'),
    onClose,
    note: error ?? footerMeta,
    noteTone: error ? 'error' : 'muted',
    testId: 'mcp-add-modal',
    dirty,
    acknowledge: ack.node,
    actions: (<>
      <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>{t('common.cancel')}</button>
      <PrimaryAction label={defaultsMode ? t('connectorLibrary.editor.save') : t('bots.connectors.mcp.connect')}
                     busyLabel={defaultsMode ? t('common.saving') : t('bots.connectors.mcp.connecting')}
                     busy={saving} disabled={!canSubmit} onClick={submit} testId="mcp-add-submit" />
    </>),
    children: (<>
      {error && <span hidden data-testid="mcp-add-error">{error}</span>}
      {banner}
      <DialogColumns>
        <DialogColumn>
          <Field label={t('bots.connectors.mcp.nameLabel')} htmlFor="mcp-add-name">
            <input id="mcp-add-name" className="form-input" value={name} onChange={e => setName(e.target.value)}
                   placeholder={t('bots.connectors.mcp.namePlaceholder')} maxLength={MAX_NAME_LENGTH} dir="auto"
                   data-testid="mcp-add-name" />
          </Field>
          {defaultsMode && (
            <Field label={t('connectorLibrary.editor.descriptionLabel')} hint={t('connectorLibrary.editor.descriptionHint')}
                   htmlFor="mcp-add-description">
              <textarea id="mcp-add-description" className="form-input form-textarea" rows={2} value={description}
                        onChange={e => setDescription(e.target.value)} maxLength={LIBRARY_DESCRIPTION_MAX} dir="auto"
                        data-testid="library-description" />
            </Field>
          )}
          {layerMode ? (
            <>
              <Field label={t('bots.connectors.mcp.urlLabel')} hint={setBy}>
                <ReadOnlyValue mono testId="mcp-add-url-readonly">{existing?.url}</ReadOnlyValue>
              </Field>
              {signInSummary}
            </>
          ) : (<>
          <Field label={t('bots.connectors.mcp.urlLabel')} htmlFor="mcp-add-url"
                 hint={asksAddressKey ? t(existing?.has_shared_credential ? 'connectorLibrary.signin.addressDropsKey'
                   : 'connectorLibrary.signin.addressCheckKey') : undefined}>
            <input id="mcp-add-url" className="form-input form-input--mono" value={url}
                   onChange={e => setUrl(e.target.value)} placeholder="https://example.com/mcp" dir="ltr"
                   data-testid="mcp-add-url" />
          </Field>
          {asksAddressKey && (
            <div data-testid="library-address-key">
              <CredentialFields entry={{ kind: 'mcp', auth_header_name: existing?.auth_header_name }}
                                value={{ value: addressKey }} onChange={v => setAddressKey(v.value ?? '')} />
            </div>
          )}
          {defaultsMode ? signInSummary : (
            <Field label={t('bots.connectors.mcp.authLabel')} hint={t('bots.connectors.mcp.authHint')}>
              <SignInChoice kind="mcp" scope="agent" value={signIn} onChange={setSignIn} name="mcp-auth" />
            </Field>
          )}
          </>)}
          <GuideField value={guide} onChange={setGuide}>
            <GuideDraft kind="mcp" name={name} description={description} current={guide} onUse={setGuide}
                        scope={draftScope} testId="mcp-guide-draft"
                        emptyHint={existing ? undefined : t('bots.connectors.guideDraft.connectFirst')}
                        tools={(existing?.tools || []).filter(tl => !offTools.has(tl.name))
                          .map(tl => ({ name: tl.name, description: tl.description }))} />
          </GuideField>
        </DialogColumn>

        {defaultsMode && existing ? (
          <DialogColumn title={t('connectorLibrary.editor.toolsTitle')}
                        badge={t('bots.connectors.mcp.toolCount', { count: existing.tools?.length ?? 0 })}>
            {(existing.tools || []).length === 0 ? (
              <p className="cxd__empty">{t('connectorLibrary.editor.noTools')}</p>
            ) : (
              <ul className="cxc__tools cxl-tool-list" data-testid="library-mcp-tools">
                {(existing.tools || []).map(tool => (
                  <li key={tool.name} className={`cxc__tool ${offTools.has(tool.name) ? 'is-off' : ''}`}>
                    <span className="cxc__tool-text">
                      <code className="cxc__tool-name" dir="ltr">{tool.name}</code>
                      {tool.description && <span className="cxc__tool-desc bidi-clamp" dir="auto">{tool.description}</span>}
                    </span>
                    <Switch size="small" checked={!offTools.has(tool.name)} onChange={() => toggleTool(tool.name)}
                            data-testid={`library-mcp-tool-${tool.name}`} />
                  </li>
                ))}
              </ul>
            )}
            <p className="cxd__aside-note">{t('connectorLibrary.editor.toolsNote')}</p>
          </DialogColumn>
        ) : (
          <DialogColumn aside>
            <h3 className="cxd__aside-title">{t('bots.connectors.mcp.howTitle')}</h3>
            <ol className="cxd__steps">
              <li className="cxd__step">{t('bots.connectors.mcp.how1')}</li>
              <li className="cxd__step">{defaultsMode ? t('connectorLibrary.editor.mcpHow2') : t('bots.connectors.mcp.how2')}</li>
              <li className="cxd__step">{t('bots.connectors.mcp.how3')}</li>
            </ol>
            <p className="cxd__aside-note">{t('bots.connectors.mcp.howNote')}</p>
          </DialogColumn>
        )}
      </DialogColumns>
    </>),
  });
}

const MAX_GUIDE_CHARS = 6000;

/** The guide for the agent — the same field as an API connector's. */
function GuideField({ value, onChange, children }: {
  value: string; onChange: (next: string) => void; children?: ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <Field label={t('bots.connectors.api.instructionsLabel')} hint={t('bots.connectors.mcp.instructionsHint')}
           htmlFor="mcp-instructions">
      <textarea id="mcp-instructions" className="form-input form-textarea" rows={6} value={value}
                placeholder={t('bots.connectors.mcp.guidePlaceholder')}
                onChange={e => onChange(e.target.value)} maxLength={MAX_GUIDE_CHARS} dir="auto"
                data-testid="mcp-instructions" />
      <p className="cxd__count" dir="ltr">{value.length} / {MAX_GUIDE_CHARS}</p>
      {children}
    </Field>
  );
}

/** One connector's guide, edited on its row. On a connector that follows
    the organization's list it shows the list's guide; saving a change asks
    first (the connector then keeps its own settings). */
function GuideEditor({ chatbotId, connector, busy, disabled, onCancel, onSave }: {
  chatbotId: string; connector: McpConnector; busy: boolean; disabled?: boolean;
  onCancel: () => void; onSave: (text: string) => void;
}) {
  const { t } = useLanguage();
  const [text, setText] = useState(connector.instructions || '');
  const following = connector.library?.chip === 'shared';
  const changed = text.trim() !== (connector.instructions || '').trim();
  return (
    <div className="cxl-synced cxc__guide" data-testid={`mcp-guide-editor-${connector.slug}`}>
      <p className="cxl-synced__text">
        {following && connector.library?.organization
          ? t(connector.instructions ? 'bots.connectors.mcp.guideFromList' : 'bots.connectors.mcp.guideNoneFromList',
            { organization: connector.library.organization })
          : t('bots.connectors.mcp.instructionsHint')}
      </p>
      <textarea className="form-input form-textarea" rows={6} value={text} dir="auto" maxLength={MAX_GUIDE_CHARS}
                placeholder={t('bots.connectors.mcp.guidePlaceholder')}
                onChange={e => setText(e.target.value)} disabled={disabled || busy}
                aria-label={t('bots.connectors.api.instructionsLabel')} data-testid={`mcp-guide-text-${connector.slug}`} />
      <GuideDraft kind="mcp" name={connector.name} current={text} onUse={setText} disabled={disabled || busy}
                  scope={{ chatbotId, connectorId: connector.connector_id }} testId={`mcp-guide-draft-${connector.slug}`}
                  tools={(connector.tools || []).filter(tl => tl.enabled)
                    .map(tl => ({ name: tl.name, description: tl.description }))} />
      <div className="cxl-synced__actions">
        <button type="button" className="cx-btn cx-btn--sm" onClick={onCancel}>{t('common.cancel')}</button>
        <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" disabled={disabled || busy || !changed}
                onClick={() => onSave(text)} data-testid={`mcp-guide-save-${connector.slug}`}>
          {t('common.save')}
        </button>
        {following && <span className="cxl-synced__hint">{t('connectorLibrary.synced.customizeHint')}</span>}
      </div>
    </div>
  );
}

function PlugGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2v6" />
      <path d="M15 2v6" />
      <path d="M6 8h12v3a6 6 0 0 1-12 0V8Z" />
      <path d="M12 17v5" />
    </svg>
  );
}
